import React from 'react'
import { useState } from 'react'
import { isMobile } from 'react-device-detect'
import { getInventoryCycleDetails } from 'client/utils/api-routes-inventory'
import {
  DcFlexBox,
  DcButton,
  PixelText,
  Section,
  SectionContent,
  DcDiv,
  PixelTable,
  DcText,
  colors,
  PixelImage,
  PixelSpinner,
} from 'pixel-base'
import { useLocalStorage } from 'shared/hooks'
import { IInventoryCycleDetail, UserInfo } from '../../../../../../decl.d'
interface IInventoryCycleDetailResponse {
  data: IInventoryCycleDetail[]
}

interface InventoryCycleDetailProps {
  cycleId: number | null
  cycleDetailData: {
    info: IInventoryCycleDetailResponse
    data: IInventoryCycleDetail[]
  }
  onClose: () => void
  onApprove?: (cycleId: number) => void
  onReject?: (cycleId: number) => void
  detailLoading: boolean
  setDetailLoading: (loading: boolean) => void
}

const InventoryCycleDetail: React.FC<InventoryCycleDetailProps> = ({
  cycleId,
  cycleDetailData,
  onClose: _onClose,
  onApprove,
  onReject,
  detailLoading,
  setDetailLoading,
}) => {
  const [cycleData, setCycleData] = useState<IInventoryCycleDetail[] | null>(
    null,
  )
  const [cycleInfo, setCycleInfo] = useState<{
    id: number
    status: string
    total_products: number
    total_matched_products_count: number
    total_unmatched_products_count: number
    total_warehouse_products?: number
    created_at: { date: string }
    created_by: number
    warehouse: {
      name: string
      warehouse_code: string
    }
  } | null>(null)

  React.useEffect(() => {
    let isCancelled = false
    const fetchDetails = async () => {
      try {
        const detailsResponse = await getInventoryCycleDetails(
          cycleId!,
        ).toPromise()
        if (!isCancelled && detailsResponse && detailsResponse.response) {
          const detailData: IInventoryCycleDetailResponse =
            detailsResponse.response as IInventoryCycleDetailResponse
          setCycleData(detailData.data)

          // Extract cycle info from the first item if available
          if (detailData.data && detailData.data.length > 0) {
            const firstItem = detailData.data[0]
            setCycleInfo(firstItem.inventory_cycle_id)
          }

          setDetailLoading(false)
        }
      } catch (error) {
        setDetailLoading(false)
      }
    }
    if (cycleId) fetchDetails()
    return () => {
      if (cycleDetailData.info) {
        // fallback to provided info if effect is cleaned up before fetch finishes
        setCycleInfo(cycleDetailData.info as any)
      }
      setCycleData(cycleDetailData.data)
      isCancelled = true
    }
  }, [cycleId, cycleDetailData])

  // get current user id from uselocal storage hook
  const [user] = useLocalStorage<UserInfo | null>('user_info', null)
  const currentUser = user?.id
  if (!cycleData) {
    return (
      <DcFlexBox justifyContent="center" alignItems="center" height="100%">
        <PixelSpinner isShow={true} />
      </DcFlexBox>
    )
  }

  // Define table columns for product details
  const productColumns = [
    {
      dataKey: 'id',
      name: 'ID',
      width: '7%',
      sortable: true,
      selector: (row: IInventoryCycleDetail) => row.id,
    },
    {
      dataKey: 'product_image_path',
      name: 'Image',
      width: '7%',
      sortable: true,
      selector: (row: IInventoryCycleDetail) => (
        <PixelImage
          src={row?.product_id?.product_image_path}
          width="auto"
          height="50px"
        />
      ),
    },
    {
      dataKey: 'product_name',
      name: 'Name',
      width: '20%',
      sortable: true,
      selector: (row: IInventoryCycleDetail) => (
        <PixelText multiLine toolTipText={row.product_id?.product_name}>
          {row.product_id?.product_name}
        </PixelText>
      ),
    },
    {
      dataKey: 'product_sku',
      name: 'SKU',
      width: '15%',
      sortable: true,
      selector: (row: IInventoryCycleDetail) => row.product_sku,
    },
    {
      dataKey: 'physical_count',
      name: 'Physical',
      width: '10%',
      sortable: true,
      selector: (row: IInventoryCycleDetail) => (
        <DcFlexBox flexDirection="column" alignItems="center">
          <PixelText style={{ fontWeight: 'bold' }}>
            {row?.physical_count || 0}
          </PixelText>
        </DcFlexBox>
      ),
    },
    {
      dataKey: 'pixelone_count',
      name: 'PixelOne',
      width: '10%',
      sortable: true,
      selector: (row: IInventoryCycleDetail) => (
        <DcFlexBox flexDirection="column" alignItems="center">
          <PixelText style={{ fontWeight: 'bold' }}>
            {row?.pixelone_count || 0}
          </PixelText>
        </DcFlexBox>
      ),
    },
    {
      dataKey: 'unmatched_products_count',
      name: 'Difference ',
      width: '12%',
      sortable: true,
      selector: (row: IInventoryCycleDetail) => (
        <DcFlexBox flexDirection="column" alignItems="center">
          <PixelText
            style={{
              fontWeight: 'bold',
              color:
                (row?.unmatched_products_count || 0) !== 0 ? 'red' : 'inherit',
            }}
          >
            {row?.unmatched_products_count || 0}
          </PixelText>
        </DcFlexBox>
      ),
    },
    {
      dataKey: 'reason_for_discrepancy',
      name: 'Reason',
      width: '15%',
      sortable: false,
      selector: (row: IInventoryCycleDetail) => (
        <DcText multiLine toolTipText={row.reason_for_discrepancy || ''}>
          {(row?.unmatched_products_count || 0) !== 0
            ? row.reason_for_discrepancy || '--'
            : '--'}
        </DcText>
      ),
    },
  ]
  return (
    <DcFlexBox
      justifyContent="flex-start"
      padding="0px"
      flexDirection="column"
      gap="20px"
      height={isMobile ? 'calc(100vh - 65px)' : '100%'}
      overflowY="auto"
    >
      {/* Topbar Section */}
      <SectionContent style={{ padding: '5px 20px' }}>
        <DcFlexBox
          gap="5px"
          height="fit-content"
          justifyContent="flex-end"
          padding="0px"
        >
          {cycleInfo?.status === 'draft' && onApprove && onReject && (
            <>
              <DcButton
                size="sm"
                variant="outline-success"
                onClick={() => {
                  if (
                    currentUser !==
                    cycleData[0]?.inventory_cycle_id?.approver_id
                  ) {
                    return alert('You are not authorized to approve this cycle')
                  }
                  onApprove(cycleInfo?.id)
                }}
              >
                Approve
              </DcButton>
              <DcButton
                size="sm"
                variant="outline-danger"
                onClick={() => {
                  if (
                    currentUser !==
                    cycleData[0]?.inventory_cycle_id?.approver_id
                  ) {
                    return alert('You are not authorized to reject this cycle')
                  }
                  onReject(cycleInfo?.id)
                }}
              >
                Reject
              </DcButton>
            </>
          )}
        </DcFlexBox>
      </SectionContent>

      {/* Header Card Section */}
      <Section>
        <SectionContent>
          <DcFlexBox
            height="auto"
            flexDirection="column"
            style={{
              backgroundColor: colors.grayLight,
              borderRadius: '12px',
              padding: '24px',
              border: `1px solid ${colors.grayBorderLight}`,
            }}
          >
            <DcFlexBox
              justifyContent="space-between"
              alignItems="center"
              width="100%"
              height="auto"
            >
              {/* Left side - Title and basic info */}
              <DcFlexBox
                style={{
                  borderBottom: `1px solid ${colors.grayBorderLight}`,
                  marginBottom: '20px',
                  paddingBottom: '10px',
                  height: 'auto',
                  flexDirection: 'column',
                  gap: '5px',
                }}
              >
                <PixelText
                  style={{
                    fontSize: '24px',
                    fontWeight: 'bold',
                    color: colors.grayDarker,
                  }}
                >
                  Cycle #{cycleInfo?.id}
                </PixelText>
                <PixelText
                  style={{ fontSize: '14px', color: colors.grayMedium }}
                >
                  {cycleInfo?.warehouse?.name} -{' '}
                  {cycleInfo?.warehouse?.warehouse_code}
                </PixelText>
              </DcFlexBox>

              {/* Right side - Action buttons */}
            </DcFlexBox>
            {/* Summary Stats Row */}
            <DcFlexBox
              flexDirection={isMobile ? 'column' : 'row'}
              height="auto"
              gap="20px"
              justifyContent="space-around"
            >
              <DcFlexBox
                height="auto"
                flexDirection="column"
                alignItems="center"
                style={{
                  backgroundColor: 'white',
                  padding: '16px',
                  borderRadius: '8px',
                  minWidth: '120px',
                  border: `1px solid ${colors.grayBorderLight}`,
                }}
              >
                <PixelText
                  style={{
                    fontSize: '14px',
                    color: colors.grayMedium,
                    marginBottom: '8px',
                  }}
                >
                  Total Products
                </PixelText>
                <PixelText
                  style={{
                    fontSize: '32px',
                    fontWeight: 'bold',
                    color: colors.grayDarker,
                  }}
                >
                  {cycleInfo?.total_products}
                </PixelText>
              </DcFlexBox>

              <DcFlexBox
                height="auto"
                flexDirection="column"
                alignItems="center"
                style={{
                  backgroundColor: 'white',
                  padding: '16px',
                  borderRadius: '8px',
                  minWidth: '120px',
                  border: `1px solid ${colors.grayBorderLight}`,
                }}
              >
                <PixelText
                  style={{
                    fontSize: '14px',
                    color: colors.grayMedium,
                    marginBottom: '8px',
                  }}
                >
                  Warehouse Products
                </PixelText>
                <PixelText
                  style={{
                    fontSize: '32px',
                    fontWeight: 'bold',
                    color: colors.primary,
                  }}
                >
                  {cycleInfo?.total_warehouse_products || 0}
                </PixelText>
              </DcFlexBox>

              <DcFlexBox
                height="auto"
                flexDirection="column"
                alignItems="center"
                style={{
                  backgroundColor: 'white',
                  padding: '16px',
                  borderRadius: '8px',
                  minWidth: '120px',
                  border: `1px solid ${colors.grayBorderLight}`,
                }}
              >
                <PixelText
                  style={{
                    fontSize: '14px',
                    color: colors.grayMedium,
                    marginBottom: '8px',
                  }}
                >
                  Matched
                </PixelText>
                <PixelText
                  style={{
                    fontSize: '32px',
                    fontWeight: 'bold',
                    color: colors.success,
                  }}
                >
                  {cycleInfo?.total_matched_products_count || 0}
                </PixelText>
              </DcFlexBox>

              <DcFlexBox
                height="auto"
                flexDirection="column"
                alignItems="center"
                style={{
                  backgroundColor: 'white',
                  padding: '16px',
                  borderRadius: '8px',
                  minWidth: '120px',
                  border: `1px solid ${colors.grayBorderLight}`,
                }}
              >
                <PixelText
                  style={{
                    fontSize: '14px',
                    color: colors.grayMedium,
                    marginBottom: '8px',
                  }}
                >
                  Unmatched
                </PixelText>
                <PixelText
                  style={{
                    fontSize: '32px',
                    fontWeight: 'bold',
                    color: colors.danger,
                  }}
                >
                  {cycleInfo?.total_unmatched_products_count || 0}
                </PixelText>
              </DcFlexBox>

              <DcFlexBox
                height="auto"
                flexDirection="column"
                alignItems="center"
                style={{
                  backgroundColor: 'white',
                  padding: '16px',
                  borderRadius: '8px',
                  minWidth: '120px',
                  border: `1px solid ${colors.grayBorderLight}`,
                }}
              >
                <PixelText
                  style={{
                    fontSize: '14px',
                    color: colors.grayMedium,
                    marginBottom: '8px',
                  }}
                >
                  Status
                </PixelText>
                <PixelText
                  style={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color:
                      cycleInfo?.status === 'completed'
                        ? colors.success
                        : cycleInfo?.status === 'rejected'
                        ? colors.danger
                        : cycleInfo?.status === 'draft'
                        ? colors.danger
                        : colors.primary,
                  }}
                >
                  {cycleInfo?.status
                    ? cycleInfo.status.charAt(0).toUpperCase() +
                      cycleInfo.status.slice(1)
                    : 'Unknown'}
                </PixelText>
              </DcFlexBox>
            </DcFlexBox>

            {/* Footer Info Row */}
            <DcFlexBox
              height="auto"
              justifyContent="space-between"
              alignItems="center"
              style={{
                marginTop: '20px',
                paddingTop: '20px',
                borderTop: `1px solid ${colors.grayBorderLight}`,
              }}
            >
              <DcFlexBox
                height="auto"
                flexDirection="row"
                gap="5px"
                justifyContent="space-between"
              >
                <PixelText
                  style={{ fontSize: '12px', color: colors.grayMedium }}
                >
                  Created at:{' '}
                  {cycleInfo?.created_at
                    ? new Date(cycleInfo.created_at.date).toLocaleString()
                    : 'N/A'}
                </PixelText>
                <PixelText
                  style={{ fontSize: '12px', color: colors.grayMedium }}
                >
                  Created by: {cycleInfo?.created_by || 'N/A'}
                </PixelText>
              </DcFlexBox>
            </DcFlexBox>
          </DcFlexBox>
        </SectionContent>
      </Section>

      {/* Product Details Table Section */}
      <Section>
        <SectionContent>
          <DcFlexBox
            height="auto"
            justifyContent="space-between"
            alignItems="center"
            style={{ marginBottom: '15px' }}
          >
            <PixelText
              style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: colors.grayDarker,
              }}
            >
              Product Details
            </PixelText>
            <PixelText style={{ fontSize: '14px', color: colors.grayMedium }}>
              {cycleData?.length} products found
            </PixelText>
          </DcFlexBox>

          {detailLoading ? (
            <DcFlexBox
              justifyContent="center"
              alignItems="center"
              height="200px"
            >
              <PixelText>Loading product details...</PixelText>
            </DcFlexBox>
          ) : (
            <DcDiv
              style={{
                border: `1px solid ${colors.grayBorderLight}`,
                borderRadius: '8px',
                overflow: 'hidden',
              }}
            >
              <PixelTable
                columns={productColumns}
                data={cycleData}
                pagination={false}
                highlightOnHover={true}
                pointerOnHover={false}
                fixedHeader
                fixedHeaderScrollHeight="calc(100vh - 600px)"
              />
            </DcDiv>
          )}
        </SectionContent>
      </Section>
    </DcFlexBox>
  )
}

export default InventoryCycleDetail
